import React, { useState, useEffect, useRef } from "react"
import { Link } from "react-router-dom"
import {
  HelpCircle,
  MessageSquare,
  Search,
  FileText,
  LifeBuoy,
  CreditCard,
  BookOpen,
  Users
} from "lucide-react"
import { Input } from "../../../ui/input"
import { Button } from "../../../ui/button"
import { Card, CardContent } from "../../../ui/card"
import HelpdeskFAQ from "./helpdesk-faq"
import ContactForm from "./contact-form"
import ChatDrawer from "./chat-drawer"
import { useIsMobile } from "../../../hooks/use-mobile"
import { useToast } from "../../../hooks/use-toast"

const TopicCard = ({ icon, title, description, linkTo }) => {
  return (
    <Link to={linkTo} className="block h-full no-underline">
      <Card
        className="border border-solid border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 bg-white h-full group"
        tabIndex={0}
        role="button"
        aria-label={`Learn about ${title}`}
      >
        <CardContent className="p-6 flex flex-col items-center text-center">
          <div className="w-12 h-12 rounded-full bg-[#F4F0FA] flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
            {icon}
          </div>
          <h3 className="font-semibold text-[#3D2A60] mb-2">{title}</h3>
          <p className="text-gray-600 text-sm">{description}</p>
        </CardContent>
      </Card>
    </Link>
  )
}

const placeholders = [
  "creator onboarding...",
  "how to get paid...",
  "troubleshoot login...",
  "upload content...",
  "manage subscriptions..."
]

const HelpdeskContent = () => {
  const [placeholderIndex, setPlaceholderIndex] = useState(0)
  const [currentPlaceholder, setCurrentPlaceholder] = useState(placeholders[0])
  const [chatOpen, setChatOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [hasSearched, setHasSearched] = useState(false)
  const [searchResults, setSearchResults] = useState([])
  const isMobile = useIsMobile()
  const searchInputRef = useRef(null)
  const { toast } = useToast()

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prevIndex => (prevIndex + 1) % placeholders.length)
    }, 3000)

    return () => clearInterval(interval)
  }, [])

  useEffect(() => {
    setCurrentPlaceholder(placeholders[placeholderIndex])
  }, [placeholderIndex])

  const handleSearch = e => {
    if (e) e.preventDefault()

    if (!searchQuery.trim()) {
      toast({
        title: "Please enter a search term",
        description: "Type what you're looking for to get started"
      })
      return
    }

    setHasSearched(true)
    // This is where you would normally fetch actual search results
    // For demo purposes, we're just simulating it
    const mockResults = simulateSearch(searchQuery)
    setSearchResults(mockResults)

    if (mockResults.length === 0) {
      toast({
        title: "No results found",
        description: `We couldn't find any matches for "${searchQuery}"`
      })
    } else {
      toast({
        title: "Search results",
        description: `Found ${mockResults.length} results for "${searchQuery}"`
      })
    }
  }

  // Simulate search functionality - In a real app, this would be an API call
  const simulateSearch = query => {
    const allPossibleResults = [
      "Getting Started: Your first steps",
      "Getting Started: Account setup",
      "Getting Started: Verification process",
      "Payments: Payout methods",
      "Payments: Tax information",
      "Payments: Invoicing",
      "Your Dashboard: Analytics overview",
      "Your Dashboard: Performance metrics",
      "Creator Community: Forums",
      "Creator Community: Events",
      "Troubleshooting: Login issues",
      "Troubleshooting: Content upload errors",
      "Contact Support: Ticket system"
    ]

    const lowercaseQuery = query.toLowerCase()
    return allPossibleResults.filter(result =>
      result.toLowerCase().includes(lowercaseQuery)
    )
  }

  // const handleTopicClick = title => {
  //   // Set the search query to the topic title
  //   setSearchQuery(title)

  //   // Focus the input (optional)
  //   if (searchInputRef.current) {
  //     searchInputRef.current.focus()
  //   }

  //   // Trigger search with a slight delay for better UX
  //   setTimeout(() => {
  //     handleSearch()
  //   }, 300)
  // }

  return (
    <main className="flex-1">
      {/* Hero Section with Updated Soft Light Purple Gradient */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#FDF8FF] to-[#EEDDF8] py-12 md:py-20">
        {/* Abstract Background Shapes */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full bg-[#F4F0FA]/30 blur-3xl"></div>
          <div className="absolute bottom-10 right-20 w-80 h-80 rounded-full bg-[#E563A4]/10 blur-3xl"></div>
          <div className="absolute top-40 right-10 w-40 h-40 rounded-full bg-[#FEC6A1]/10 blur-3xl"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-5xl font-bold mb-4 md:mb-5 text-[#3D2A60]">
              How can we{" "}
              <span className="text-[#E563A4] inline-block hover:scale-105 transition-all duration-300">
                help
              </span>{" "}
              you?
            </h1>

            <p className="text-lg md:text-xl text-[#523880] max-w-2xl mx-auto mb-6 md:mb-10">
              We're here to make things easy. Search anything or just say
              hi—we're listening 💜
            </p>

            {/* Enhanced Search Bar - Responsive */}
            <form
              onSubmit={handleSearch}
              className="max-w-2xl mx-auto relative"
            >
              <Input
                ref={searchInputRef}
                type="text"
                placeholder={currentPlaceholder}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-12 pr-4 py-5 md:py-7 h-12 md:h-16 rounded-full border border-solid border-white/40 bg-white/30 text-[#3D2A60] placeholder:text-[#6E59A5] focus:outline-none focus:ring-2 focus:ring-[#E563A4] shadow-lg transition-all duration-300"
                aria-label="Search help topics"
              />
              <Search className="absolute left-4 md:left-5 top-3 md:top-5 h-5 md:h-6 w-5 md:w-6 text-[#6E59A5]" />
              <Button
                type="submit"
                className="absolute right-1 md:right-2 top-1 md:top-2 h-10 md:h-12 bg-[#E563A4] hover:bg-[#d44b92] rounded-full px-3 md:px-6 font-medium text-sm md:text-base transition-all duration-300"
              >
                {isMobile ? "Search" : "Let's find it!"}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Animated Popular Topics Section - Responsive Grid */}
      <section className="py-10 md:py-16 bg-gradient-to-b from-white to-[#F4F0FA]/50">
        <div className="container mx-auto px-4">
          <h2 className="text-xl md:text-3xl font-bold text-center mb-6 md:mb-10 text-[#3D2A60]">
            Popular Topics
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6">
            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.1s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<BookOpen className="h-6 w-6 text-[#3D2A60]" />}
                title="Getting Started"
                description="Everything you need to hit the ground running."
                linkTo="/help/getting-started"
              />
            </div>

            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.2s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<CreditCard className="h-6 w-6 text-[#3D2A60]" />}
                title="Payments"
                description="How to get paid and manage your earnings."
                linkTo="/help/payments"
              />
            </div>

            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.3s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<FileText className="h-6 w-6 text-[#3D2A60]" />}
                title="Your Dashboard"
                description="Navigate and make the most of your dashboard."
                linkTo="/help/dashboard"
              />
            </div>

            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.4s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<Users className="h-6 w-6 text-[#3D2A60]" />}
                title="Creator Community"
                description="Connect with other creators and learn from them."
                linkTo="/help/community"
              />
            </div>

            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.5s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<LifeBuoy className="h-6 w-6 text-[#3D2A60]" />}
                title="Troubleshooting"
                description="Quick fixes for common issues you might encounter."
                linkTo="/help/troubleshooting"
              />
            </div>

            <div
              className="animate-fade-up"
              style={{
                animationDelay: "0.6s",
                animationFillMode: "forwards",
                opacity: 0
              }}
            >
              <TopicCard
                icon={<MessageSquare className="h-6 w-6 text-[#3D2A60]" />}
                title="Contact Support"
                description="Need more help? We're just a message away."
                linkTo="/help/contact-support"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Search Results Section - Only shown when search is performed */}
      {hasSearched && (
        <section className="py-10 md:py-16 bg-white">
          <div className="container mx-auto px-4">
            <h2 className="text-xl md:text-2xl font-bold mb-6 text-[#3D2A60]">
              Results for "{searchQuery}"
            </h2>

            {searchResults.length > 0 ? (
              <div className="space-y-4">
                {searchResults.map((result, index) => (
                  <Card
                    key={index}
                    className="border border-solid border-gray-100 hover:border-[#E563A4]/30 transition-all cursor-pointer"
                  >
                    <CardContent className="p-4 md:p-6">
                      <h3 className="font-medium text-[#3D2A60]">{result}</h3>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-10">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#F4F0FA] mb-4">
                  <HelpCircle className="h-8 w-8 text-[#3D2A60]" />
                </div>
                <h3 className="text-xl font-medium text-[#3D2A60] mb-2">
                  No results found
                </h3>
                <p className="text-gray-600">
                  We couldn't find any matches for "{searchQuery}". Try another
                  search or browse the popular topics.
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Content Section - FAQs and Contact */}
      <section className="py-10 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* FAQ Section - Takes full width on mobile, 2/3 on desktop */}
            <div className="lg:col-span-2 space-y-6 md:space-y-8">
              <h2 className="text-xl md:text-2xl font-bold mb-6 md:mb-8 text-[#3D2A60]">
                Frequently Asked Questions
              </h2>
              <HelpdeskFAQ />
            </div>

            {/* Contact Section - Takes full width on mobile, 1/3 on desktop */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-6 md:p-8 border border-solid border-[#F4F0FA] max-w-[600px] mx-auto">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-[#F4F0FA] mb-4 transform transition-transform hover:scale-105 duration-300">
                    <MessageSquare className="h-8 w-8 text-[#E563A4]" />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-[#3D2A60] mb-2">
                    Still need help?
                  </h3>
                  <p className="text-gray-600">
                    Our support team is ready to assist you
                  </p>
                </div>

                {/* Contact Options with improved spacing */}
                <div className="space-y-6">
                  {/* Chat Button */}
                  <Button
                    className="w-full py-6 bg-[#3D2A60] hover:bg-[#2A1C42] flex items-center justify-center gap-3 text-base font-medium transition-all duration-300 shadow-sm hover:shadow touch-target rounded-lg"
                    onClick={() => setChatOpen(true)}
                  >
                    <MessageSquare className="h-5 w-5" />
                    Start Chat
                  </Button>

                  <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-b-0 border-l-0 border-r-0 border-solid border-[#F4F0FA]"></div>
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">
                        or send a message
                      </span>
                    </div>
                  </div>

                  {/* Contact Form with improved spacing */}
                  <div className="bg-white rounded-lg">
                    <ContactForm />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Chat Drawer */}
      <ChatDrawer open={chatOpen} onOpenChange={setChatOpen} />
    </main>
  )
}

export default HelpdeskContent
